import { useState } from 'react';
import { PropertyFilters as PropertyFiltersType } from '@/hooks/useProperties';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Filter, X } from 'lucide-react';

interface PropertyFiltersProps {
  onFilterChange: (filters: PropertyFiltersType) => void;
  onReset: () => void;
}

const PROPERTY_TYPES = [
  { value: 'house', label: 'House' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'condo', label: 'Condo' },
  { value: 'villa', label: 'Villa' },
  { value: 'land', label: 'Land' },
  { value: 'commercial', label: 'Commercial' },
];

const PROVINCES = [
  'La Altagracia',
  'Santo Domingo',
  'Puerto Plata',
  'Samaná',
  'La Romana',
  'Santiago',
];

export const PropertyFilters = ({ onFilterChange, onReset }: PropertyFiltersProps) => {
  const [filters, setFilters] = useState<PropertyFiltersType>({});

  const handlePropertyTypeChange = (type: string, checked: boolean) => {
    const currentTypes = filters.property_type || [];
    const newTypes = checked
      ? [...currentTypes, type]
      : currentTypes.filter(t => t !== type);
    
    const newFilters = { ...filters, property_type: newTypes.length > 0 ? newTypes : undefined };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleTransactionTypeChange = (value: string) => {
    const newFilters = { ...filters, transaction_type: value !== 'all' ? value : undefined };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handlePriceChange = (type: 'min' | 'max', value: string) => {
    const numValue = value ? parseFloat(value) : undefined;
    const newFilters = {
      ...filters,
      [type === 'min' ? 'min_price' : 'max_price']: numValue,
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleBedroomsChange = (value: string) => {
    const newFilters = { ...filters, bedrooms: value !== 'any' ? parseInt(value) : undefined };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleBathroomsChange = (value: string) => {
    const newFilters = { ...filters, bathrooms: value !== 'any' ? parseInt(value) : undefined };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleReset = () => {
    setFilters({});
    onReset();
  };

  const hasActiveFilters = Object.keys(filters).length > 0;

  return (
    <Card className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Filters</h3>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleReset}>
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Property Type */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Property Type</Label>
        <div className="space-y-2">
          {PROPERTY_TYPES.map(type => (
            <div key={type.value} className="flex items-center space-x-2">
              <Checkbox
                id={`type-${type.value}`}
                checked={filters.property_type?.includes(type.value)}
                onCheckedChange={(checked) => 
                  handlePropertyTypeChange(type.value, checked as boolean)
                }
              />
              <Label 
                htmlFor={`type-${type.value}`}
                className="text-sm font-normal cursor-pointer"
              >
                {type.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Transaction Type */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Transaction Type</Label>
        <RadioGroup value={filters.transaction_type || 'all'} onValueChange={handleTransactionTypeChange}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="all" id="all" />
            <Label htmlFor="all" className="text-sm font-normal cursor-pointer">All</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="sale" id="sale" />
            <Label htmlFor="sale" className="text-sm font-normal cursor-pointer">For Sale</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="rent" id="rent" />
            <Label htmlFor="rent" className="text-sm font-normal cursor-pointer">For Rent</Label>
          </div>
        </RadioGroup>
      </div>

      {/* Price Range */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Price Range (USD)</Label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor="min-price" className="text-xs">Min</Label>
            <Input
              id="min-price"
              type="number"
              placeholder="Min"
              value={filters.min_price || ''}
              onChange={(e) => handlePriceChange('min', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="max-price" className="text-xs">Max</Label>
            <Input
              id="max-price"
              type="number"
              placeholder="Max"
              value={filters.max_price || ''}
              onChange={(e) => handlePriceChange('max', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Bedrooms */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Bedrooms</Label>
        <Select value={filters.bedrooms?.toString() || 'any'} onValueChange={handleBedroomsChange}>
          <SelectTrigger>
            <SelectValue placeholder="Any" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any</SelectItem>
            <SelectItem value="1">1+</SelectItem>
            <SelectItem value="2">2+</SelectItem>
            <SelectItem value="3">3+</SelectItem>
            <SelectItem value="4">4+</SelectItem>
            <SelectItem value="5">5+</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bathrooms */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Bathrooms</Label>
        <Select value={filters.bathrooms?.toString() || 'any'} onValueChange={handleBathroomsChange}>
          <SelectTrigger>
            <SelectValue placeholder="Any" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any</SelectItem>
            <SelectItem value="1">1+</SelectItem>
            <SelectItem value="2">2+</SelectItem>
            <SelectItem value="3">3+</SelectItem>
            <SelectItem value="4">4+</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </Card>
  );
};
